/**
 * App Initialization for CSR Architecture
 * Initializes browser storage, auth store, and sandbox service worker
 */

import { browserDB } from '../services/browser-storage/indexedDB';
import { localStorage } from '../services/browser-storage/localStorage';
import { useAuthStore } from '../stores/authStore';
import { useCodeGenStore } from '../stores/codeGenStore';
import { BrowserSandboxService } from '../services/sandbox/browserSandbox';
import { logger } from './logger';

export interface AppInitializationResult {
  browserStorageReady: boolean;
  authStoreInitialized: boolean;
  codeGenStoreInitialized: boolean;
  sandboxServiceWorkerRegistered: boolean;
  errors: string[];
}

/**
 * Initialize the application with all required browser services
 */
export async function initializeApp(): Promise<AppInitializationResult> {
  const result: AppInitializationResult = {
    browserStorageReady: false,
    authStoreInitialized: false,
    codeGenStoreInitialized: false,
    sandboxServiceWorkerRegistered: false,
    errors: []
  };

  logger.info('🚀 Initializing app for CSR architecture...');

  // 1. Initialize IndexedDB
  try {
    await initializeBrowserStorage();
    result.browserStorageReady = true;
    logger.info('✅ Browser storage initialized');
  } catch (error) {
    const errorMsg = `Failed to initialize browser storage: ${error instanceof Error ? error.message : 'Unknown error'}`;
    result.errors.push(errorMsg);
    logger.error('❌ Browser storage initialization failed:', error);
  }

  // 2. Initialize Auth Store
  try {
    await initializeAuthStore();
    result.authStoreInitialized = true;
    logger.info('✅ Auth store initialized');
  } catch (error) {
    const errorMsg = `Failed to initialize auth store: ${error instanceof Error ? error.message : 'Unknown error'}`;
    result.errors.push(errorMsg);
    logger.error('❌ Auth store initialization failed:', error);
  }

  // 3. Initialize Code Generation Store
  try {
    await initializeCodeGenStore();
    result.codeGenStoreInitialized = true;
    logger.info('✅ Code generation store initialized');
  } catch (error) {
    const errorMsg = `Failed to initialize code generation store: ${error instanceof Error ? error.message : 'Unknown error'}`;
    result.errors.push(errorMsg);
    logger.error('❌ Code generation store initialization failed:', error);
  }

  // 4. Initialize Sandbox Service Worker
  try {
    await initializeSandboxServiceWorker();
    result.sandboxServiceWorkerRegistered = true;
    logger.info('✅ Sandbox service worker registered');
  } catch (error) {
    const errorMsg = `Failed to register sandbox service worker: ${error instanceof Error ? error.message : 'Unknown error'}`;
    result.errors.push(errorMsg);
    logger.error('❌ Sandbox service worker registration failed:', error);
  }

  // Log summary
  const successCount = Object.values(result).filter(v => v === true).length;
  const totalServices = 4;
  
  if (result.errors.length === 0) {
    logger.info(`🎉 App initialization complete! All ${totalServices} services ready.`);
  } else {
    logger.warn(`⚠️ App initialization completed with ${result.errors.length} errors. ${successCount}/${totalServices} services ready.`);
  }

  return result;
}

/**
 * Initialize browser storage (IndexedDB and LocalStorage)
 */
async function initializeBrowserStorage(): Promise<void> {
  // Test IndexedDB connection
  try {
    await browserDB.open();
    logger.debug('IndexedDB connection established');
  } catch (error) {
    throw new Error(`IndexedDB initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  // Test LocalStorage
  try {
    await localStorage.put('_init_test', 'test_value');
    const testValue = await localStorage.get<string>('_init_test');
    if (testValue !== 'test_value') {
      throw new Error('LocalStorage read/write test failed');
    }
    await localStorage.delete('_init_test');
    logger.debug('LocalStorage test passed');
  } catch (error) {
    throw new Error(`LocalStorage initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Initialize authentication store
 */
async function initializeAuthStore(): Promise<void> {
  try {
    // The auth store initializes itself on creation, but we can verify it's ready
    const authStore = useAuthStore.getState();
    
    // Wait for initialization to complete
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max
    
    while (!authStore.isInitialized && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    
    if (!authStore.isInitialized) {
      throw new Error('Auth store initialization timeout');
    }
    
    logger.debug('Auth store ready, authenticated:', authStore.isAuthenticated);
  } catch (error) {
    throw new Error(`Auth store initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Initialize code generation store
 */
async function initializeCodeGenStore(): Promise<void> {
  try {
    // The code generation store is ready immediately
    const _codeGenStore = useCodeGenStore.getState();
    void _codeGenStore;
    logger.debug('Code generation store ready');
  } catch (error) {
    throw new Error(`Code generation store initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Initialize sandbox service worker
 */
async function initializeSandboxServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers not supported in this browser');
  }

  try {
    // Create sandbox service instance (this will register the service worker)
    const _sandboxService = new BrowserSandboxService();
    void _sandboxService;
    
    // Wait a bit for registration to complete
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Verify service worker is registered
    const registration = await navigator.serviceWorker.getRegistration('/sandbox-sw.js');
    if (!registration) {
      throw new Error('Service worker registration not found');
    }
    
    logger.debug('Sandbox service worker registered successfully');
  } catch (error) {
    throw new Error(`Sandbox service worker registration failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Check if the app is properly initialized
 */
export function isAppInitialized(): boolean {
  try {
    const authStore = useAuthStore.getState();
    const codeGenStore = useCodeGenStore.getState();
    
    return authStore.isInitialized && 
           typeof codeGenStore !== 'undefined' &&
           'serviceWorker' in navigator;
  } catch {
    return false;
  }
}

/**
 * Get initialization status for debugging
 */
export function getInitializationStatus(): Record<string, boolean> {
  try {
    const authStore = useAuthStore.getState();
    const codeGenStore = useCodeGenStore.getState();
    
    return {
      authStoreInitialized: authStore.isInitialized,
      codeGenStoreReady: typeof codeGenStore !== 'undefined',
      serviceWorkerSupported: 'serviceWorker' in navigator,
      indexedDBSupported: 'indexedDB' in window,
      localStorageSupported: 'localStorage' in window
    };
  } catch (error) {
    logger.error('Failed to get initialization status:', error);
    return {
      authStoreInitialized: false,
      codeGenStoreReady: false,
      serviceWorkerSupported: false,
      indexedDBSupported: false,
      localStorageSupported: false
    };
  }
}
